import React from 'react'
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { t } from 'ttag'
import { FormData } from '../../ChallengeAdmin'
import { List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import { CustomTypeCategory } from '../../../../services/CustomTypes'
import { isSimpleDayType } from '../../../../common/helpers/challenges'

import { ReactComponent as TotalTargetActiveIcon } from '../../../../assets/v3/icons/total-target-active.svg'
import { ReactComponent as TotalTargetInactiveIcon } from '../../../../assets/v3/icons/total-target-inactive.svg'
import { ReactComponent as ConsistencyTargetActiveIcon } from '../../../../assets/v3/icons/consistency-target-active.svg'
import { ReactComponent as ConsistencyTargetInactiveIcon } from '../../../../assets/v3/icons/consistency-target-inactive.svg'
import { ReactComponent as CollectiveTargetActiveIcon } from '../../../../assets/v3/icons/collective-target-active.svg'
import { ReactComponent as CollectiveTargetInactiveIcon } from '../../../../assets/v3/icons/collective-target-inactive.svg'

import YumMediumTitle from '../../../../components/YumMediumTitle/YumMediumTitle'
import { CustomType, ProgressType } from '../../../../services/api.types'

interface ChallengeStepChallengeTypeProps {
  register: UseFormRegister<FormData>
  control: Control<FormData>
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  originalProgressType: ProgressType | null
}

export default function ChallengeStepChallengeType(props: ChallengeStepChallengeTypeProps) {
  const { watch, setValue, originalProgressType } = props

  // Watch the goal type and activity type to determine which options should be enabled
  const goalType = watch('goalType')
  const activityType = watch('activityType')

  const dailyTargetDisabled =
    activityType === CustomTypeCategory.CALORIES || activityType === CustomTypeCategory.TOTAL_POINTS

  return (
    <>
      {/* <YumMediumTitle>{t`Challenge type`}</YumMediumTitle> */}
      <List component="div" role="radiogroup" aria-label={t`Challenge type`}>
        {/* Total target */}
        <ListItem
          disabled={isSimpleDayType(activityType as CustomTypeCategory)}
          role="radio"
          aria-checked={goalType === 'totalAmount'}
          dense
          button
          onClick={() => setValue('goalType', 'totalAmount')}
          selected={goalType === 'totalAmount'}
          style={{ alignItems: 'flex-start' }}
        >
          <ListItemIcon style={{ marginTop: '8px' }}>
            {goalType === 'totalAmount' ? <TotalTargetActiveIcon /> : <TotalTargetInactiveIcon />}
          </ListItemIcon>
          <ListItemText
            primary={t`Total target`}
            secondary={t`Progress is based on the total amount accumulated over the whole challenge period. All results for the selected unit are summed up.`}
            secondaryTypographyProps={{ style: { fontSize: '0.875rem', marginTop: '4px' } }}
          />
        </ListItem>
        {/* Daily target */}
        <ListItem
          disabled={dailyTargetDisabled}
          role="radio"
          aria-checked={goalType === 'consistentDays'}
          dense
          button
          onClick={() => setValue('goalType', 'consistentDays')}
          selected={goalType === 'consistentDays'}
          style={{ alignItems: 'flex-start' }}
        >
          <ListItemIcon style={{ marginTop: '8px' }}>
            {goalType === 'consistentDays' ? <ConsistencyTargetActiveIcon /> : <ConsistencyTargetInactiveIcon />}
          </ListItemIcon>
          <ListItemText
            primary={t`Daily target`}
            secondary={t`Progress is based on how many days a defined daily goal is reached or exceeded. Results are calculated from the number of days the daily goal is met.`}
            secondaryTypographyProps={{ style: { fontSize: '0.875rem', marginTop: '4px' } }}
          />
        </ListItem>
        {dailyTargetDisabled && originalProgressType !== ProgressType.COLLECTIVE_CUSTOM && (
          <div style={{ textAlign: 'center', opacity: 0.5 }}>{t`This activity type supports total targets only.`}</div>
        )}
        {/* Legacy Collective */}
        {originalProgressType === ProgressType.COLLECTIVE_CUSTOM && (
          <ListItem
            role="radio"
            aria-checked={goalType === 'collective'}
            dense
            button
            onClick={() => setValue('goalType', 'collective')}
            selected={goalType === 'collective'}
            style={{ alignItems: 'flex-start' }}
          >
            <ListItemIcon style={{ marginTop: '8px' }}>
              {goalType === 'collective' ? <CollectiveTargetActiveIcon /> : <CollectiveTargetInactiveIcon />}
            </ListItemIcon>
            <ListItemText
              primary={t`Collective target`}
              secondary={t`Progress is based on the total amount accumulated collectively over the entire challenge period.`}
              secondaryTypographyProps={{ style: { fontSize: '0.875rem', marginTop: '4px' } }}
            />
          </ListItem>
        )}
      </List>
    </>
  )
}
